import React, { useState, useEffect } from "react";
import classnames from "classnames";
import uniqueId from "lodash/uniqueId";
import Clickable, { ClickableAction } from "@unum/clickable";
import AccessibilityLabel from "@unum/accessibility-label";
import { useBreakpoint } from "@unum/hooks";
import SearchBox from "@unum/search-box";
import Heading from "@unum/heading";
import Spring from "@unum/spring";
import styles from "./PrimaryNavStrip.module.scss";
import logo from "./assets/unum-logo.svg";
import cross from "./assets/cross.svg";
import search from "./assets/search.svg";

type MenuItem = {
  label: React.ReactNode;
  action?: ClickableAction;
  links?: undefined;
};

export type Navigation = {
  label: React.ReactNode;
  links: (Navigation | MenuItem)[];
  action?: undefined;
};

type Props = {
  menu?: Navigation;
  footerMenu?: Navigation;
  onSearch?: (term: string) => void;
  promo?: React.ReactNode;
  logoAction?: ClickableAction;
  renderSearch?: boolean;
};

type PromoProps = {
  children: React.ReactNode;
  title: string;
  footer: React.ReactNode;
};

const PrimaryNavStrip = ({
  menu,
  footerMenu,
  onSearch,
  promo,
  logoAction,
  renderSearch = true
}: Props) => {
  const [active, setActive] = useState<number | null>(null);
  const [subMenuActive, setSubMenuActive] = useState<number | null>(null);
  const [isMobileSearchActive, setMobileSearchActive] =
    useState<boolean>(false);
  const breakpoint = useBreakpoint();

  const closeMenu = (event) => {
    if (
      active !== null &&
      (event.key === "Escape" ||
        event.srcElement.className === "primary-nav-strip--active")
    ) {
      return setActive(null);
    }
  };

  useEffect(() => {
    document.addEventListener("keyup", closeMenu, false);
    document.addEventListener("click", closeMenu, false);

    return () => {
      document.removeEventListener("keyup", closeMenu, false);
      document.removeEventListener("click", closeMenu, false);
    };
  }, [active]);

  return (
    <div
      // @note: This follows the current implementation where the modifier
      // is not on the block element.
      className={classnames({ "primary-nav-strip--active": active !== null })}
    >
      <header className={classnames("primary-nav-strip", styles["Menu"])}>
        <Spring>
          <div
            className={classnames(
              "primary-nav-strip__wrapper",
              styles["wrapper"]
            )}
          >
            <div className={classnames("primary-nav-strip__logo-menu")}>
              <Clickable
                className={classnames("primary-nav-strip__logo-link")}
                {...logoAction}
              >
                <img
                  src={logo}
                  className={classnames(
                    "primary-nav-strip__logo",
                    styles["logo"]
                  )}
                  alt="Unum logo"
                />
                <AccessibilityLabel>Homepage</AccessibilityLabel>
              </Clickable>
              {(breakpoint === "sm" || breakpoint === "xs") && (
                <>
                  {menu && menu.label && (
                    <Clickable
                      className={classnames(
                        "button primary-nav-strip__menu-button",
                        {
                          "primary-nav-strip__menu-button--active":
                            active != null
                        }
                      )}
                      onClick={() => {
                        setActive((currentIndex) => {
                          if (currentIndex === 1) {
                            return null;
                          }
                          return 1;
                        });
                        renderSearch && setMobileSearchActive(false);
                      }}
                    >
                      {menu.label}
                    </Clickable>
                  )}
                  {renderSearch && (
                    <Clickable
                      className={classnames(
                        "button primary-nav-strip__search-button",
                        {
                          "primary-nav-strip__search-button--active":
                            isMobileSearchActive
                        }
                      )}
                      onClick={() => {
                        setMobileSearchActive(() => {
                          if (isMobileSearchActive) {
                            return false;
                          }
                          return true;
                        });
                        setActive(null);
                        setSubMenuActive(null);
                      }}
                    >
                      <span className="primary-nav-strip__search-button-wrap">
                        <img
                          className="primary-nav-strip__search-button-icon primary-nav-strip__search-button-icon--search"
                          src={search}
                          role="presentation"
                          alt=""
                        />
                        <img
                          className="primary-nav-strip__search-button-icon primary-nav-strip__search-button-icon--cross"
                          src={cross}
                          role="presentation"
                          alt=""
                        />
                        <AccessibilityLabel>
                          Search unum.co.uk
                        </AccessibilityLabel>
                      </span>
                    </Clickable>
                  )}
                </>
              )}
            </div>
            {menu && (
              <nav>
                <ul
                  className={classnames(
                    styles["main-menu"],
                    "list",
                    "primary-nav-strip__main-menu"
                  )}
                >
                  {menu.links.map((link, index) => {
                    if (link.action) {
                      return (
                        <li key={uniqueId()}>
                          <Clickable
                            className={classnames(
                              styles["menu-button"],
                              "primary-nav-strip__main-menu-link"
                            )}
                            {...link.action}
                          >
                            {link.label}
                          </Clickable>
                        </li>
                      );
                    }

                    return (
                      <li key={uniqueId()}>
                        <Clickable
                          className={classnames(
                            styles["menu-button"],
                            "primary-nav-strip__main-menu-link--with-chevron",
                            "primary-nav-strip__main-menu-link",
                            {
                              "primary-nav-strip__main-menu-link--active":
                                active === index
                            }
                          )}
                          onClick={() => {
                            setActive((currentIndex) => {
                              if (currentIndex === index) return null;
                              return index;
                            });
                          }}
                        >
                          {link.label}
                        </Clickable>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            )}
            {renderSearch &&
              (breakpoint === "md" ||
                breakpoint === "lg" ||
                breakpoint === "xl") && (
                <div className="primary-nav-strip__search-form">
                  <SearchBox onSubmit={onSearch} />
                </div>
              )}
          </div>
        </Spring>
        {renderSearch && (breakpoint === "sm" || breakpoint === "xs") && (
          <div
            className={classnames(styles["mobile-search-form-wrapper"], {
              [styles["mobile-search-form-wrapper--active"]]:
                isMobileSearchActive
            })}
          >
            <SearchBox onSubmit={onSearch} />
          </div>
        )}
        {menu && (
          <div
            className={classnames(styles["mega-menu-wrapper"], {
              [styles["mega-menu-wrapper--active"]]: active != null
            })}
          >
            <div className={classnames(styles["mega-menu-main"])}>
              <Spring>
                <div className="row">
                  <div className="col-xs-12 col-md-6 col-md-offset-2">
                    {menu.links.map((subMenu, menuIndex) => {
                      if (subMenu.links) {
                        return (
                          <div
                            key={uniqueId()}
                            className={classnames("row", styles["sub-menu"], {
                              [styles["active"]]: active === menuIndex
                            })}
                          >
                            {(breakpoint === "sm" || breakpoint === "xs") && (
                              <div className="col-xs-12">
                                <Clickable
                                  className={classnames(
                                    styles["mobile-menu-button"],
                                    "primary-nav-strip__main-menu-link",
                                    "primary-nav-strip__main-menu-link--with-chevron",
                                    {
                                      "primary-nav-strip__main-menu-link--active":
                                        subMenuActive === menuIndex
                                    }
                                  )}
                                  onClick={() => {
                                    setSubMenuActive((currentIndex) => {
                                      if (currentIndex === menuIndex)
                                        return null;
                                      return menuIndex;
                                    });
                                  }}
                                >
                                  {subMenu.label}
                                </Clickable>
                              </div>
                            )}
                            {subMenu.links.map((subSubMenu) => {
                              if (!subSubMenu.links) {
                                return null;
                              }

                              return (
                                <div
                                  key={uniqueId()}
                                  className="col-xs-12 col-md-6"
                                >
                                  <div
                                    className={classnames(
                                      styles["sub-menu-links-wrapper"],
                                      {
                                        [styles[
                                          "sub-menu-links-wrapper--active"
                                        ]]: subMenuActive === menuIndex
                                      }
                                    )}
                                  >
                                    <Heading
                                      component="div"
                                      level={5}
                                      className={classnames(
                                        "heading-default",
                                        "primary-nav-strip__submenu-title",
                                        "small-grey-heading"
                                      )}
                                    >
                                      {subSubMenu.label}
                                    </Heading>
                                    <ul
                                      className={classnames(
                                        "list",
                                        "primary-nav-strip__submenu-links"
                                      )}
                                    >
                                      {subSubMenu.links.map(
                                        (innerLink, innerLinkIndex) => {
                                          if (innerLink.links) {
                                            return null;
                                          }
                                          return (
                                            <li key={uniqueId()}>
                                              <Clickable
                                                {...innerLink.action}
                                                className={classnames(
                                                  "primary-nav-strip__submenu-link"
                                                )}
                                              >
                                                {innerLink.label}
                                              </Clickable>
                                            </li>
                                          );
                                        }
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      if (subMenu.action) {
                        return (
                          <div
                            key={uniqueId()}
                            className={classnames("row", styles["sub-menu"])}
                          >
                            {(breakpoint === "sm" || breakpoint === "xs") && (
                              <div className="col-xs-12">
                                <Clickable
                                  className={classnames(
                                    styles["mobile-menu-button"],
                                    styles["mobile-menu-button--no-chevron"],
                                    "primary-nav-strip__main-menu-link"
                                  )}
                                  {...subMenu.action}
                                >
                                  {subMenu.label}
                                </Clickable>
                              </div>
                            )}
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                  {promo && (
                    <div className="col-xs-12 col-md-4">
                      <div className="primary-nav-strip__promo">{promo}</div>
                    </div>
                  )}
                </div>
              </Spring>
            </div>
            {footerMenu && (
              <div className={classnames("nav-footer")}>
                <Spring>
                  <div className="row">
                    <div className="col-xs-12 col-md-offset-2 col-md-10">
                      <div className={classnames("nav-footer__wrapper")}>
                        <Heading
                          component="h4"
                          className={classnames(
                            "heading-default",
                            "nav-footer__title"
                          )}
                        >
                          {footerMenu.label}
                        </Heading>
                        <ul className={classnames("list", "nav-footer__links")}>
                          {footerMenu.links.map((link, index) => {
                            return (
                              <li key={uniqueId()}>
                                <Clickable
                                  {...link.action}
                                  className={classnames("nav-footer__link")}
                                >
                                  {link.label}
                                </Clickable>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Spring>
              </div>
            )}
          </div>
        )}
      </header>
    </div>
  );
};

const Promo = ({ children, title, footer }: PromoProps) => {
  const [isActive, setActive] = useState<boolean>(false);
  const breakpoint = useBreakpoint();
  return (
    <div
      className={classnames("nav-promo", styles["Promo"], {
        "nav-promo nav-promo--active": isActive
      })}
    >
      {breakpoint === "sm" || breakpoint === "xs" ? (
        <Clickable
          className={classnames("h5 nav-promo__title", styles["menu-button"])}
          onClick={() => {
            setActive(() => {
              if (isActive) {
                return false;
              }
              return true;
            });
          }}
        >
          {title}
        </Clickable>
      ) : (
        <h3 className="h5 nav-promo__title">{title}</h3>
      )}
      <div className="nav-promo__wrap">
        <p className="nav-promo__description">{children}</p>
      </div>
      {footer && (
        <div className="nav-promo__footer">
          <div
            className={classnames(
              "nav-promo__description",
              styles["nav-promo-footer"]
            )}
          >
            {footer}
          </div>
        </div>
      )}
    </div>
  );
};

PrimaryNavStrip.Promo = Promo;

export default PrimaryNavStrip;
